import { useContext, useMemo } from 'react';
import { AnalyticsContext } from '@shared/core';
import { useEventInfo } from '@shared/utils/eventInfo';
import { addError } from '@shared/utils/logger';

/**
 * Custom hook to generate a unique client reference string
 * by combining the event ID and the anonymous user ID.
 *
 * @param {string} [eventIdArg] - Optional event ID to override the one from event context. It required if hook is call without event info context.
 * @returns {{
 *   clientReference: string;
 *   loadingClientReference: boolean;
 *   anonymousId?: string;
 *   eventId?: string;
 * }}
 *
 * @example
 * const { clientReference, loadingClientReference } = useClientReference();
 */
export function useClientReference(eventIdArg?: string) {
  const { eventInfo, loading: loadingClientReference } = useEventInfo();
  const { anonymousId } = useContext(AnalyticsContext);

  const clientReference = useMemo(
    function () {
      const eventId = eventIdArg || eventInfo?.eventId || '';
      const clientReference = `${eventId}_${anonymousId}`;

      if (!eventId && !loadingClientReference) {
        if (eventIdArg !== undefined) {
          // eslint-disable-next-line no-console
          console.log('ClientReference - Missing eventIdArg');
          addError('ClientReference - Missing eventIdArg');
        } else {
          // eslint-disable-next-line no-console
          console.log('ClientReference - Missing eventId');
          addError('ClientReference - Missing eventId');
        }
      }

      if (!anonymousId) {
        // eslint-disable-next-line no-console
        console.log('ClientReference - Missing anonymousId');
        addError('ClientReference - Missing anonymousId');
      }

      return clientReference;
    },
    [anonymousId, eventIdArg, eventInfo?.eventId, loadingClientReference],
  );

  return { clientReference, loadingClientReference, anonymousId, eventId: eventInfo?.eventId };
}
